// Canonical analytics names shared by every package. This is the single source
// of truth for Segment event and page names.
//
// To add a new event: add a KEY: 'Display Value' entry to the EVENT_TYPES object
// below, then use it as eventTypes.KEY. To add a new page, add an entry to
// PAGE_TYPES and use pageTypes.KEY. The AnalyticsEvent / AnalyticsPage union
// types pick up the new value automatically.
const EVENT_TYPES = {
    ADD_NEW_AGENT: 'Add New Agent', // when add new button is clicked
    CREATE_AGENT: 'Create Agent', // When a new agent is created
    UPDATE_AGENT: 'Update Agent',
    DELETE_AGENT: 'Delete Agent',
    DUPLICATE_AGENT: 'Duplicate Agent',
    IMPORT_AGENT: 'Import Agent',
    EXPORT_AGENT: 'Export Agent',
    CREATE_TEMPLATE: 'Create Template',
    DELETE_TEMPLATE: 'Delete Template',
    SEND_PROMPT: 'Send Prompt',
    USER_SIGNUP: 'User Signup',
    USER_LOGIN: 'User Login',
    BILLING_PORTAL_OPENED: 'Billing Portal Opened',
    SHARED_AGENT: 'Shared Agent',
    UNSHARE_AGENT: 'Unshare Agent',
    NEW_CHATFLOW_TEMPLATE_USED: 'New Chatflow Template Used',
    NEW_CHATFLOW_NODE_USED: 'New Chatflow Node Used',
    CHATFLOW_EMBED_MODEL_OPENED: 'Chatflow Embed Model Opened',
    AGENT_CHATBOT_SELECTED: 'Agent Chatbot Selected',
    ADD_NEW_TOOL: 'Add New Tool',
    TOOL_SELECTED: 'Tool Selected',
    TOOL_DELETED: 'Tool Deleted',
    TOOL_CREATED: 'Tool Created',
    TOOL_UPDATED: 'Tool Updated',
    TOOL_TESTED: 'Tool Tested',
    TOOL_ACTION_TESTED: 'Tool Action Tested',
    TOOL_DUPLICATED: 'Tool Duplicated',
    TOOL_SHARED: 'Tool Shared',
    TOOL_UNSHARED: 'Tool Unshared',
    TOOL_ACTION_ADDED: 'Tool Action Added',
    TOOL_RENAMED: 'Tool Renamed',
    TOOL_INPUT_ADDED: 'Tool Input Added',
    PROJECT_LOAD: 'Projects Load',
    PROJECT_CREATED: 'Project Created',
    WORKFLOW_RAN: 'Workflow Ran',
    TASK_ACTION: 'Task Action',
    TASK_DETAIL: 'Task Detail',
    TASK_RERUN: 'Task Rerun',
    TEAM_SELECTED: 'Team Selected',
    TEAM_NEW: 'Team New',
    TEAM_CREATED: 'Team Created',
    TEAM_UPDATED: 'Team Updated',
    TEAM_DELETED: 'Team Deleted',
    TEAM_DUPLICATED: 'Team Duplicated',
    TEAM_EXPORTED: 'Team Exported',
    TEAM_SHARED: 'Team Shared',
    TEAM_UNSHARED: 'Team Unshared',
    TEAM_STEP_ADDED: 'Team Step Added',
    TEAM_STEP_DELETED: 'Team Step Deleted',
    TEAM_STEP_TEAMMATE_ASSIGNED: 'Team Step Teammate Assigned',
    TEAM_RUN_STARTED: 'Team Run Started',
    TEAM_RUN_MODAL_OPENED: 'Team Run Modal Opened',
    TEAM_STEP_APPROVED: 'Team Step Approved',
    TEAM_STEP_OUTPUT_EDITED: 'Team Step Output Edited',
    TEAM_STEP_RERUN: 'Team Step Rerun',
    TEAM_RUN_VIEWED: 'Team Run Viewed',
    TEAM_SHARED_MARKETPLACE_VIEWED: 'Team Shared Marketplace Viewed',
    TEAM_SCHEDULE_CREATED: 'Team Schedule Created',
    TEAM_SCHEDULE_UPDATED: 'Team Schedule Updated',
    TEAM_SCHEDULE_TOGGLED: 'Team Schedule Toggled',
    TEAM_SCHEDULE_DELETED: 'Team Schedule Deleted',
    VIEW_TEAMMATE_FROM_TEAM_RUN: 'View Teammate From Team Run',
    CHAT_WITH_TEAMMATE_OPENED: 'Chat With Teammate Opened',
    ADD_NEW_SKILL: 'Add New Skill',
    SKILL_SELECTED: 'Skill Selected',
    SKILL_CREATED: 'Skill Created',
    SKILL_UPDATED: 'Skill Updated',
    SKILL_DELETED: 'Skill Deleted',
    SKILL_SHARED: 'Skill Shared',
    SKILL_UNSHARED: 'Skill Unshared',
    SKILL_SHARED_WITH_GROUP: 'Skill Shared With Group',
    SKILL_UNSHARED_FROM_GROUP: 'Skill Unshared From Group',
    SKILL_DUPLICATED: 'Skill Duplicated',
    SKILL_EXPORTED: 'Skill Exported',
    SKILL_IMPORTED: 'Skill Imported',
    SIGNUP_FAILED: 'Signup Failed',
    LOGIN_FAILED: 'Login Failed',
    USER_LOGOUT: 'User Logout',
    EMAIL_VERIFIED: 'Email Verified',
    EMAIL_VERIFICATION_FAILED: 'Email Verification Failed',
    VERIFICATION_CODE_RESENT: 'Verification Code Resent',
    ORGANIZATION_JOIN_STARTED: 'Organization Join Started',
    ORGANIZATION_JOINED: 'Organization Joined',
    ORGANIZATION_JOIN_FAILED: 'Organization Join Failed',
    ORGANIZATION_INACTIVE_VIEWED: 'Organization Inactive Viewed',
    SUPPORT_CONTACTED: 'Support Contacted',
    PASSWORD_RESET_REQUESTED: 'Password Reset Requested',
    PASSWORD_RESET_COMPLETED: 'Password Reset Completed',
    API_KEY_CREATED: 'API Key Created',
    API_KEY_COPIED: 'API Key Copied',
    API_KEY_DELETED: 'API Key Deleted',
    API_KEY_UPDATED: 'API Key Updated',
    SECRET_CREATED: 'Secret Created',
    SECRET_UPDATED: 'Secret Updated',
    SECRET_DELETED: 'Secret Deleted',
    ORGANIZATION_MEMBER_ADDED: 'Organization Member Added',
    ORGANIZATION_MEMBER_ROLE_CHANGED: 'Organization Member Role Changed',
    ORGANIZATION_MEMBER_REMOVED: 'Organization Member Removed',
    ORGANIZATION_GROUP_CREATED: 'Organization Group Created',
    ORGANIZATION_GROUP_UPDATED: 'Organization Group Updated',
    ORGANIZATION_GROUP_DELETED: 'Organization Group Deleted',
    ORGANIZATION_GROUP_MEMBER_ADDED: 'Organization Group Member Added',
    ORGANIZATION_GROUP_MEMBER_REMOVED: 'Organization Group Member Removed',
    ORGANIZATION_SETTINGS_UPDATED: 'Organization Settings Updated',
    ORGANIZATION_DOMAIN_DELETED: 'Organization Domain Deleted',
    ORGANIZATION_SWITCHED: 'Organization Switched',
    ACCOUNT_PROFILE_UPDATED: 'Account Profile Updated',
    PASSWORD_CHANGED: 'Password Changed',
    MEMORY_UPDATED: 'Memory Updated',
    MEMORY_DELETED: 'Memory Deleted',
    MEMORY_GRAPH_VIEWED: 'Memory Graph Viewed',
    SKILL_TOOL_ADDED: 'Skill Tool Added',
    SKILL_TOOL_REMOVED: 'Skill Tool Removed',
    SKILL_CREATE_CANCELLED: 'Skill Create Cancelled',
    APP_ERROR: 'App Error',
    PROMPT_RESPONSE_COMPLETED: 'Prompt Response Completed',
    PROMPT_RESPONSE_FAILED: 'Prompt Response Failed',
    NOT_FOUND_VIEWED: 'Not Found Viewed',
    MESSAGE_FEEDBACK_SUBMITTED: 'Message Feedback Submitted',
    TOOL_CALL_APPROVED: 'Tool Call Approved',
    TOOL_CALL_REJECTED: 'Tool Call Rejected',
    PLAYGROUND_TEAMMATE_CREATED: 'Playground Teammate Created',
    NEW_SESSION_STARTED: 'New Session Started',
    CHAT_SESSION_SELECTED: 'Chat Session Selected',
    CHAT_SESSION_DELETED: 'Chat Session Deleted',
    CHAT_MESSAGE_COPIED: 'Chat Message Copied',
    PROMPT_FILE_ATTACHED: 'Prompt File Attached',
    SUBTEAMMATE_INTERACTION: 'SubTeammate Interaction',
    PLAYGROUND_QUICK_PICK_SELECTED: 'Playground Quick Pick Selected',
    DEEP_RESEARCH_TOGGLED: 'Deep Research Toggled',
    WEB_SEARCH_TOGGLED: 'Web Search Toggled',
    GLOBAL_ASSISTANT_OPENED: 'Global Assistant Opened',
    // In-canvas agent builder events (WF-2593)
    AGENT_NODE_ADDED: 'Agent Node Added',
    AGENT_NODE_REMOVED: 'Agent Node Removed',
    AGENT_MODEL_SELECTED: 'Agent Model Selected',
    AGENT_SKILL_TOGGLED: 'Agent Skill Toggled',
    AGENT_DOCUMENT_UPLOADED: 'Agent Document Uploaded',
    AGENT_API_ACCESS_TOGGLED: 'Agent API Access Toggled',
    AGENT_SUBTEAMMATE_ADDED: 'Agent Subteammate Added',
};
const PAGE_TYPES = {
    MY_CHATBOT_AGENTS: 'My Chatbot Agents',
    EDIT_CHATBOT_AGENT: 'Edit Chatbot Agent',
    CHATBOT_AGENT_DIALOG: 'Chatbot Agent Dialog',
    TEMPLATES: 'Templates',
    TOOLS_VIEWER: 'Tools Viewer',
    TOOL_BUILDER: 'Tool Builder',
    SKILL_EDITOR: 'Skill Editor',
    SKILLS_LIST: 'Skills List',
    TEAMS_LIST: 'Teams List',
    TEAM_BUILD: 'Team Build',
    TEAM_RUN: 'Team Run',
    TEAM_HISTORY: 'Team History',
    PLAYGROUND: 'Playground',
    ACCOUNT: 'Account',
    ORGANIZATION_SETTINGS: 'Organization Settings',
    ORGANIZATION_USAGE: 'Organization Usage',
    API_KEYS: 'Keys & Secrets',
    MEMORY: 'Memory',
    LOGIN: 'Login',
    REGISTER: 'Register',
    EMAIL_VERIFICATION: 'Email Verification',
    FORGOT_PASSWORD: 'Forgot Password',
    ORGANIZATION_INACTIVE: 'Organization Inactive',
};
// Public constants. Known keys keep their literal type (and editor autocomplete),
// while keys that legacy call sites still reference but that have not been
// migrated yet resolve to the union type, so those call sites keep compiling.
export const eventTypes = EVENT_TYPES;
export const pageTypes = PAGE_TYPES;
//# sourceMappingURL=constants.js.map